import { useEffect, useState } from 'react';
import MyApiClient from '../../../api-client/MyApiClient';
import { sports } from './enum';

/*
 * Il catalogo delle discipline sportive sta a DB (TASK #13): il Superadmin ne aggiunge
 * una e la vedono subito Manager e app, senza rilasci.
 *
 * L'elenco compilato dentro `enum.js` non sparisce: resta come punto di partenza e come
 * ripiego se la chiamata non risponde. Qui sopra ci si limita a sostituirne il contenuto
 * MANTENENDO LA STESSA REFERENCE dell'array - `splice`, non riassegnazione - perche' i
 * 67 file che fanno `import { sports }` continuino a funzionare senza toccarli.
 *
 * Il prezzo di quella scelta e' che mutare un array non fa ri-renderizzare React: per
 * questo l'idratazione deve finire PRIMA del primo render delle pagine private (ci pensa
 * `MainLayout` con `useSportsCatalogReady`), e chi vuole reagire ai cambiamenti usa
 * `useSportsCatalog`.
 */

const CACHE_KEY = 'sportsCatalog';

/*
 * Oltre questo tempo si va avanti con l'elenco compilato e il catalogo, se arriva,
 * arriva dopo. Serve perche' `MainLayout` aspetta questa promessa prima del primo
 * render: senza un tetto, una chiamata che non risponde - non che fallisce: che resta
 * appesa, e axios di suo non ha timeout - lascerebbe il Manager sul preloader per
 * sempre. Meglio un catalogo vecchio di un gestionale che non si apre.
 */
const HYDRATION_TIMEOUT_MS = 5000;

/*
 * `inCorso`  : la richiesta in volo, per non farne due insieme.
 * `aggiornato`: il catalogo del SERVER e' stato applicato almeno una volta.
 *
 * Sono due cose distinte di proposito. Ricordarsi anche i tentativi ANDATI MALE
 * avvelenerebbe la sessione: un solo 401 - o una chiamata partita prima che ci
 * fosse il token - lascerebbe il gestionale sull'elenco compilato fino al
 * ricaricamento della pagina, e le discipline nuove non comparirebbero piu' da
 * nessuna parte senza che niente lo segnali.
 */
let inCorso = null;
let aggiornato = false;
const listeners = new Set();

const withTimeout = (promise) =>
    new Promise((resolve) => {
        const timer = setTimeout(() => resolve(sports), HYDRATION_TIMEOUT_MS);
        const done = () => {
            clearTimeout(timer);
            resolve(sports);
        };
        promise.then(done, done);
    });

const readToken = () => {
    try {
        const details = sessionStorage.getItem('userDetails');
        return details ? JSON.parse(details).idToken : null;
    } catch (e) {
        return null;
    }
};

const applyCatalog = (catalog) => {
    const next = catalog
        .filter((sport) => sport.active)
        .map((sport) => ({ value: sport.code, label: sport.label, core: sport.core }))
        .sort((a, b) => a.label.localeCompare(b.label, 'it'));

    if (next.length === 0) return;

    sports.splice(0, sports.length, ...next);
    listeners.forEach((listener) => listener(sports));
};

/**
 * Riempie `sports` con il catalogo del server.
 *
 * Non rifiuta mai: se la chiamata fallisce si resta sull'elenco compilato, che e'
 * comunque giusto per tutte le discipline storiche. Meglio un catalogo vecchio di una
 * pagina che non si apre.
 */
export const hydrateSportsCatalog = (force = false) => {
    if (inCorso && !force) return withTimeout(inCorso);
    if (aggiornato && !force) return Promise.resolve(sports);

    inCorso = (async () => {
        try {
            // La copia in sessione serve a non far vedere l'elenco vecchio per un
            // istante al primo render: viene comunque sovrascritta dal server qui sotto.
            const cached = sessionStorage.getItem(CACHE_KEY);
            if (cached && !force) {
                try {
                    applyCatalog(JSON.parse(cached));
                } catch (e) {
                    sessionStorage.removeItem(CACHE_KEY);
                }
            }

            const token = readToken();
            if (!token) return sports;

            const catalog = await new MyApiClient().getSportsCatalog(token);
            if (Array.isArray(catalog) && catalog.length > 0) {
                applyCatalog(catalog);
                sessionStorage.setItem(CACHE_KEY, JSON.stringify(catalog));
                aggiornato = true;
            } else {
                console.warn('Catalogo discipline: risposta inattesa, si usa l\'elenco locale', catalog);
            }
        } catch (e) {
            console.error('Catalogo discipline non raggiungibile, si usa l\'elenco locale', e);
        } finally {
            // Comunque sia andata la richiesta non e' piu' in volo. Se non e' andata,
            // `aggiornato` resta false e il prossimo che chiede riprova.
            inCorso = null;
        }

        return sports;
    })();

    return withTimeout(inCorso);
};

/** Da chiamare dopo che il Superadmin ha modificato il catalogo. */
export const refreshSportsCatalog = () => hydrateSportsCatalog(true);

/**
 * Il nome della disciplina, con il codice reso leggibile come ripiego: una disciplina
 * cancellata o disattivata resta scritta nei documenti storici e va comunque mostrata.
 */
export const sportLabel = (code) => {
    if (!code) return '-';
    const found = sports.find((sport) => sport.value === code);
    if (found) return found.label;
    return String(code)
        .toLowerCase()
        .split('_')
        .filter(Boolean)
        .map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(' ');
};

/** Blocca il primo render finche' il catalogo non e' pronto (o non ha rinunciato). */
export const useSportsCatalogReady = () => {
    const [ready, setReady] = useState(aggiornato);

    useEffect(() => {
        let alive = true;
        hydrateSportsCatalog().then(() => {
            if (alive) setReady(true);
        });
        return () => {
            alive = false;
        };
    }, []);

    return ready;
};

/** Per le pagine che devono reagire a una modifica del catalogo. */
export const useSportsCatalog = () => {
    const [list, setList] = useState(sports);

    useEffect(() => {
        const listener = (next) => setList([...next]);
        listeners.add(listener);
        hydrateSportsCatalog();
        return () => {
            listeners.delete(listener);
        };
    }, []);

    return list;
};
